import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Speech-to-text for children who cannot yet type.
 *
 * Runs entirely in the browser via the Web Speech API — nothing is recorded,
 * stored, or sent anywhere by us; the transcript reaches our server only if the
 * child then presses Make it, and only as part of the brief.
 *
 * Support is uneven (absent in Firefox, and in iOS Safari below 14.5), so this
 * reports `supported` and the UI hides the microphone entirely rather than
 * offering a button that does nothing — a dead button is worse than no button
 * for a six-year-old, who will assume they did it wrong.
 */

type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
};

const getRecogniser = (): (new () => SpeechRecognitionLike) | null => {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

export function useVoice(onTranscript: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const [supported] = useState(() => getRecogniser() !== null);
  const recogniser = useRef<SpeechRecognitionLike | null>(null);
  const callback = useRef(onTranscript);
  callback.current = onTranscript;

  useEffect(() => {
    return () => {
      // A child who wanders off mid-sentence should not leave the microphone on.
      recogniser.current?.stop();
    };
  }, []);

  const start = useCallback(() => {
    const Recogniser = getRecogniser();
    if (!Recogniser) return;

    const instance = new Recogniser();
    recogniser.current = instance;
    instance.lang = navigator.language || 'en-GB';
    instance.interimResults = false;
    instance.continuous = false;

    instance.onresult = (event) => {
      const text = Array.from({ length: event.results.length }, (_, i) => event.results[i]![0]!.transcript)
        .join(' ')
        .trim();
      if (text) callback.current(text);
    };
    instance.onerror = () => setListening(false);
    instance.onend = () => setListening(false);

    try {
      instance.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, []);

  const stop = useCallback(() => {
    recogniser.current?.stop();
    setListening(false);
  }, []);

  return { supported, listening, start, stop };
}
